import { z } from 'zod';
import type { RouteDef } from './api.js';

/** Company channels (docs/company-channels.md): Slack-like rooms where agents and people talk things over. */
export const CHANNEL_POST_MAX = 2000;

const Slug = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits and dashes');

export const ChannelInput = z.object({
  slug: Slug,
  name: z.string().trim().min(1).max(80),
  topic: z.string().trim().max(300).default(''),
});
export type ChannelInput = z.infer<typeof ChannelInput>;

export const ChannelPatch = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  topic: z.string().trim().max(300).optional(),
  archived: z.boolean().optional(),
});
export type ChannelPatch = z.infer<typeof ChannelPatch>;

export const ChannelMembersInput = z.object({
  members: z.array(z.object({ agentId: z.uuid(), inContext: z.boolean().default(false) })).max(200),
});
export type ChannelMembersInput = z.infer<typeof ChannelMembersInput>;

export const ChannelPostInput = z.object({
  text: z.string().trim().min(1).max(CHANNEL_POST_MAX),
  replyToId: z.uuid().nullable().optional(),
});
export type ChannelPostInput = z.infer<typeof ChannelPostInput>;

export const ChannelPostsQuery = z.object({
  before: z.iso.datetime().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** A thread: its root post and replies. Without: the channel's top level. */
  thread: z.uuid().optional(),
});

export const ChannelSearchQuery = z.object({
  q: z.string().trim().min(2).max(200),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export interface ChannelMemberDto {
  agentId: string;
  inContext: boolean;
}

export type ChannelKind = 'channel' | 'dm';

export interface ChannelDto {
  id: string;
  slug: string;
  name: string;
  topic: string;
  /** `dm`: the private line between two agents (people read along). */
  kind: ChannelKind;
  archived: boolean;
  members: ChannelMemberDto[];
  postCount: number;
  lastPostAt: string | null;
  createdAt: string;
  createdByAgentId: string | null;
  createdByUserId: string | null;
  /** Posts by others since this user last opened the channel (top level and threads). */
  unread: number;
  /** Agents with a reply run for this channel in progress ("is typing…"). */
  activeAgentIds: string[];
}

export interface ChannelPostDto {
  id: string;
  channelId: string;
  authorAgentId: string | null;
  authorUserId: string | null;
  authorName: string;
  runId: string | null;
  body: string;
  mentions: string[];
  replyToId: string | null;
  createdAt: string;
  /** `system`: a platform line (channel created, task handed over, conversation paused). */
  kind: 'message' | 'system';
  meta: Record<string, unknown> | null;
  /** Thread roots only. */
  replyCount: number;
  lastReplyAt: string | null;
}

export interface ChannelSearchHitDto {
  id: string;
  channelId: string;
  channelName: string;
  channelKind: ChannelKind;
  authorName: string;
  body: string;
  replyToId: string | null;
  createdAt: string;
}

export const CHANNEL_ROUTES = {
  listChannels: { method: 'GET', path: '/api/channels', perm: 'conversation:read' },
  /** Managing channels and who reads along is an admin action (agent:update), audited. */
  createChannel: { method: 'POST', path: '/api/channels', perm: 'agent:update' },
  updateChannel: { method: 'PATCH', path: '/api/channels/:id', perm: 'agent:update' },
  setChannelMembers: { method: 'PUT', path: '/api/channels/:id/members', perm: 'agent:update' },
  listChannelPosts: { method: 'GET', path: '/api/channels/:id/posts', perm: 'conversation:read' },
  createChannelPost: { method: 'POST', path: '/api/channels/:id/posts', perm: 'conversation:create' },
  /** Read marker of the signed-in person (unread badges). */
  markChannelRead: { method: 'POST', path: '/api/channels/:id/read', perm: 'conversation:read' },
  searchChannelPosts: { method: 'GET', path: '/api/channels/search', perm: 'conversation:read' },
} as const satisfies Record<string, RouteDef>;
